import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { setSeoLinksForRoute } from './utils/canonical';
import { initializePerformanceOptimizations } from './utils/performance';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { initializeSEOMonitoring } from './utils/seoMonitoring';
import LoadingScreen from './components/LoadingScreen';
import './App.css';
import './colors-professional.css';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import ServicesPage from './pages/ServicesPage';
import BlogPage from './pages/BlogPage';
import PortfolioPage from './pages/PortfolioPage';
import CareersPage from './pages/CareersPage';
import CareerDetailPage from './pages/CareerDetailPage';
import PlansPage from './pages/PlansPage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';
import SemanticColorTest from './components/SemanticColorTest';
import AnimationTest from './components/AnimationTest';
import HighContrastTest from './components/HighContrastTest';
import ColorOptimizationTest from './components/ColorOptimizationTest';
import HighContrastSupport from './utils/highContrastSupport';
import './utils/testHighContrastSupport';

// Component to handle route changes
const RouteHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const normalizedPathname = location.pathname !== '/'
      ? location.pathname.replace(/\/+$/, '')
      : location.pathname;

    if (normalizedPathname !== location.pathname) {
      navigate(
        {
          pathname: normalizedPathname,
          search: location.search,
          hash: location.hash
        },
        { replace: true }
      );
      return;
    }

    // Keep canonical and language-alternate URLs aligned with the current URL.
    setSeoLinksForRoute(location.pathname);
    
    // Only scroll to top when there's no hash (for regular navigation)
    // Hash navigation is handled by individual pages
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash, location.search, navigate]);
  
  return null;
};

// Main app content component
const AppContent = () => {
  const { isLoading } = useLanguage();

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="App">
      <RouteHandler />
      <Header />
      <Routes>
        {['', '/ar'].map(prefix => (
          <React.Fragment key={prefix || 'en'}>
            <Route path={prefix || '/'} element={<Home />} />
            <Route path={`${prefix}/about`} element={<About />} />
            <Route path={`${prefix}/services`} element={<ServicesPage />} />
            <Route path={`${prefix}/blog`} element={<BlogPage />} />
            <Route path={`${prefix}/portfolio`} element={<PortfolioPage />} />
            <Route path={`${prefix}/careers`} element={<CareersPage />} />
            <Route path={`${prefix}/careers/:id`} element={<CareerDetailPage />} />
            <Route path={`${prefix}/plans`} element={<PlansPage />} />
            <Route path={`${prefix}/contact`} element={<ContactPage />} />
            <Route path={`${prefix}/semantic-test`} element={<SemanticColorTest />} />
            <Route path={`${prefix}/animation-test`} element={<AnimationTest />} />
            <Route path={`${prefix}/high-contrast-test`} element={<HighContrastTest />} />
            <Route path={`${prefix}/color-optimization-test`} element={<ColorOptimizationTest />} />
          </React.Fragment>
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
    </div>
  );
};

function App() {
  useEffect(() => {
    // Initialize high contrast support
    if (!window.highContrastSupport) {
      window.highContrastSupport = new HighContrastSupport();
    }
    
    // Initialize performance optimizations
    initializePerformanceOptimizations();
    
    // Initialize SEO monitoring in development
    if (process.env.NODE_ENV === 'development') {
      initializeSEOMonitoring();
    }
  }, []);

  return (
    <Router>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </Router>
  );
}

export default App;
